import type { ExperienceCategory } from 'app/redux/models/ExperienceCategory/ExperienceCategory';
import type { Picture } from 'app/typings';
import type { PictureView } from 'app/typings/core/common/pictureView/pictureView';
import { dayjs, format } from 'app/utils/dayjs';
import { pluralize } from 'app/utils/strings';

import type { ExperienceNameArg } from './Types';
import { DurationDescription } from './Types';

const formatDuration = (
  duration?: number,
  durationDescription?: DurationDescription | string
): string | null => {
  if (!duration || !durationDescription) {
    return null;
  }

  const durationMap: Record<string, string> = {
    [DurationDescription.MINUTE]: pluralize(duration, 'min', 'mins'),
    [DurationDescription.HOUR]: pluralize(duration, 'hour', 'hours'),
    [DurationDescription.HALF_DAY]: 'half day',
    [DurationDescription.DAY]: pluralize(duration, 'day', 'days'),

    MINUTE: pluralize(duration, 'min', 'mins'),
    HOUR: pluralize(duration, 'hour', 'hours'),
    HALF_DAY: 'half day',
    DAY: pluralize(duration, 'day', 'days'),
  };

  return durationMap[durationDescription];
};

export const computeExperienceName = ({
  category,
  quantity,
  brand,
  duration,
  durationDescription,
  time,
  locations,
}: ExperienceNameArg): string => {
  if (!category) {
    return 'Experience';
  }

  const categoryName = category.name;
  const brandString = (category.brand && brand) || '';
  const durationString =
    category.duration && duration && durationDescription
      ? `• ${formatDuration(duration, durationDescription)}`
      : '';
  const timeString =
    category.time && time
      ? `• ${
          typeof time === 'string'
            ? dayjs(time, ['HH:mm:ss', 'HH:mm']).format('HH:mm')
            : time instanceof Date
            ? format(time, 'HH:mm')
            : time.format('HH:mm')
        }`
      : '';
  const location = category.locations && locations ? `• ${locations}` : '';
  const quantityString = category.quantityTitle ? quantity : '';
  const title = [
    quantityString,
    categoryName,
    brandString,
    durationString,
    timeString,
    location,
  ]
    .filter((e) => !!e)
    .join(' ');

  return title || 'Experience';
};

export const getCategoryEmoji = (
  categoryId: number,
  experienceCategories: ExperienceCategory[],
  emojis: Picture[]
): PictureView | null => {
  const category = experienceCategories.find((exp) => exp.id === categoryId);

  if (!category) {
    return null;
  }

  const emoji = emojis.find((e) => e.id === category.pictureId);

  if (!emoji || !emoji.pictureId) {
    return null;
  }

  const date = new Date(emoji.updatedAt);

  return {
    ...emoji,
    createdAt: date,
    updatedAt: date,
    path: emoji.pictureId,
  };
};
